import type { ApiClient } from '../client.js'
import type {
  CampaignViewer,
  ClaimedCampaign,
  PublicCampaign,
} from '../types/promotion-campaign.js'

/**
 * Public campaign endpoints backing the marketing site's claim flow.
 *
 * Lookups are anonymous (PUBLIC client — no bearer, no X-Active-Context, even
 * if the visitor happens to be signed in); the claim is authenticated. Callers
 * supply the client so the web app can pick the right one per call.
 */

export type {
  CampaignViewer,
  CampaignViewerState,
  ClaimedCampaign,
  PublicCampaign,
  PublicCampaignMetadata,
  PublicCampaignState,
} from '../types/promotion-campaign.js'

export function getCampaignViewer(
  client: ApiClient,
  slug: string,
  opts: { signal?: AbortSignal } = {}
): Promise<{ viewer: CampaignViewer }> {
  return client.request<{ viewer: CampaignViewer }>(
    `/api/v1/promotions/campaigns/${encodeURIComponent(slug)}/viewer`,
    { method: 'GET', signal: opts.signal }
  )
}

export function getFeaturedCampaign(
  client: ApiClient,
  opts: { signal?: AbortSignal } = {}
): Promise<{ campaign: PublicCampaign }> {
  return client.request<{ campaign: PublicCampaign }>('/api/v1/promotions/campaigns/featured', {
    method: 'GET',
    signal: opts.signal,
  })
}

export function getCampaignBySlug(
  client: ApiClient,
  slug: string,
  opts: { signal?: AbortSignal } = {}
): Promise<{ campaign: PublicCampaign }> {
  return client.request<{ campaign: PublicCampaign }>(
    `/api/v1/promotions/campaigns/${encodeURIComponent(slug)}`,
    { method: 'GET', signal: opts.signal }
  )
}

/**
 * `deviceId` is the caller's first-party anonymous id (lowercase hex, 16–64
 * chars). Telemetry only: it lets the server record which browser became this
 * account, and the server IGNORES an absent or malformed value rather than
 * refusing the claim.
 */
export function claimCampaign(
  client: ApiClient,
  slug: string,
  opts: { signal?: AbortSignal; deviceId?: string } = {}
): Promise<ClaimedCampaign> {
  return client.request<ClaimedCampaign>(
    `/api/v1/promotions/campaigns/${encodeURIComponent(slug)}/claim`,
    {
      method: 'POST',
      body: JSON.stringify(opts.deviceId ? { deviceId: opts.deviceId } : {}),
      signal: opts.signal,
    }
  )
}
